import { useShallow } from 'zustand/react/shallow';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import { getNonCouponItems } from '@hooks/useShoppingCart/coupon';
import { useEditPlan } from '@hooks/useEditPlan/useEditPlan';
import { useEquipmentAction } from '@hooks/useEquipmentAction';
import { useProductOffersCheckout } from '@hooks/useProductOffersCheckout';
import { useProductOffersModals } from '@hooks/useProductOffersModals';
import { useEvOfferings } from '@hooks/useEvOfferings/useEvOfferings';
import { useProductOfferings } from '@hooks/useProductOfferings/useProductOfferings';
import { useSelectPlanFlow } from '@hooks/useSelectPlanFlow/useSelectPlanFlow';
import { useSessionNavigation } from '@hooks/useSessionNavigation';
import { deriveCartSummary, useShoppingCart } from '@hooks/useShoppingCart/useShoppingCart';
import { useTenantConfig } from '@hooks/useTenantConfig/useTenantConfig';
import { useTrackOrderLandingPageView } from '@hooks/useTrackOrderLandingPageView/useTrackOrderLandingPageView';
import { useSessionContext } from '@contexts/SessionContext';
import CheckAvailabilityDialog, {
  type DialogCloseReason,
} from '@components/modals/CheckAvailabilityDialog/CheckAvailabilityDialog';
import LeadCaptureDialog from '@components/modals/LeadCaptureDialog/LeadCaptureDialog';
import { ConfigurePlanDialog } from '@components/ProductOffers/configuration';
import { EvBundleRelationshipDialog } from '@components/ProductOffers/modals/EvBundleRelationshipDialog/EvBundleRelationshipDialog';
import { AddressHeader } from '@components/ProductOffers/layout/AddressHeader';
import { CartFooter } from '@components/ProductOffers/layout/CartFooter';
import { TopNavBar } from '@components/ProductOffers/layout/TopNavBar';
import { ShoppingCartDrawer } from '@components/ProductOffers/modals/ShoppingCartDrawer/ShoppingCartDrawer';
import { ProductOffersPageSkeleton } from '@components/ProductOffers/ProductOffersPageSkeleton';
import { PlansSection } from '@components/ProductOffers/plans/PlansSection/PlansSection';
import { buildSectionConfigs } from '@components/ProductOffers/sectionConfigs';
import { useProductOffersStore } from '@store/productOffersStore/productOffersStore';
import { selectQualifiedAddressId, useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import type { Plan } from '@t/productOffering.types';
import { hasInternetInCart } from '@utils/cartUtils';
import { toOrderAnalyticsQualification } from '@utils/orderAnalyticsQualification/orderAnalyticsQualification';

interface ProductOffersPageProps {
  onClose: (event?: unknown, reason?: DialogCloseReason) => void;
}

const ProductOffersPage = ({ onClose }: ProductOffersPageProps) => {
  const theme = useTheme();
  const { sessionId } = useSessionContext();
  const {
    resolvedAddressId,
    qualifiedAddressId,
    qualificationResult,
    leadCaptureContext,
    hasActiveWorkOrder: storeHasActiveWorkOrder,
    setQualificationFlow,
  } = useQualificationFlowStore(
    useShallow((s) => ({
      resolvedAddressId: s.resolvedAddressId,
      qualifiedAddressId: selectQualifiedAddressId(s),
      qualificationResult: s.qualificationResult,
      leadCaptureContext: s.leadCaptureContext,
      hasActiveWorkOrder: s.hasActiveWorkOrder,
      setQualificationFlow: s.setQualificationFlow,
    }))
  );
  const { goToStep, isPending } = useSessionNavigation(sessionId ?? '');
  const { filters, footerCheckoutError, setFooterCheckoutError } = useProductOffersStore(
    useShallow((s) => ({
      filters: s.activeCategoryFilters,
      footerCheckoutError: s.footerCheckoutError,
      setFooterCheckoutError: s.setFooterCheckoutError,
    }))
  );
  const {
    handlePlanSelected,
    handleSwitchPlan,
    openBundleForEdit,
    pendingPlanId: cardPendingPlanId,
    bundleSelectionPlan,
    bundleEditInitialIds,
    handleBundleRelationshipConfirmed,
    handleBundleRelationshipDismissed,
    isBundleConfirming,
  } = useSelectPlanFlow(sessionId ?? '');
  const { resolve: resolveEquipmentAction } = useEquipmentAction(sessionId ?? '', resolvedAddressId ?? '');

  const tenantConfig = useTenantConfig();
  const hasEvOfferings = tenantConfig.data?.hasEvOfferings ?? false;

  const { plansByCategory, isLoading, isError, error } = useProductOfferings(sessionId ?? '', resolvedAddressId ?? '');
  const { data: evOfferings = [] } = useEvOfferings(sessionId ?? '', hasEvOfferings);

  const isPendingWorkOrder = isError && error?.name === '13';

  if (isPendingWorkOrder && !storeHasActiveWorkOrder) {
    setQualificationFlow((prev) => ({ ...prev, hasActiveWorkOrder: true }));
  }
  if (!isPendingWorkOrder && storeHasActiveWorkOrder) {
    setQualificationFlow((prev) => ({ ...prev, hasActiveWorkOrder: false }));
  }

  const { cart, isCartLoading, isPending: isCartMutating } = useShoppingCart(sessionId ?? '');

  const modals = useProductOffersModals({ sessionId, cart, isPendingWorkOrder, onClose });

  useTrackOrderLandingPageView({
    sessionId,
    cart,
    qualification: toOrderAnalyticsQualification({ qualificationResult, leadCaptureContext, resolvedAddressId }),
  });

  const internetInCart = hasInternetInCart(cart);
  const evOfferingIds = new Set(evOfferings.map((p) => p.planId));
  const evInCart = cart?.cartItem?.some((item) => evOfferingIds.has(item.productOffering?.id ?? '')) ?? false;
  const regularPlansInCart =
    getNonCouponItems(cart).some(
      (item) =>
        item.type !== 'equipment' &&
        item.type !== 'installation_order' &&
        !evOfferingIds.has(item.productOffering?.id ?? '')
    );
  const cartSummary = deriveCartSummary(cart);
  const editPlan = useEditPlan(sessionId ?? '');

  const { handleCheckout } = useProductOffersCheckout({
    sessionId,
    cart,
    qualification: { qualifiedAddressId, resolvedAddressId },
    goToStep,
    onCloseCart: modals.handleCloseCart,
    setFooterCheckoutError,
    setDrawerCheckoutError: modals.setDrawerCheckoutError,
  });

  const handleEditPlan = async (plan: Plan) => {
    if (plan.bundleRelationships.length > 0) {
      openBundleForEdit(plan);
      return;
    }

    const cartItem = cart?.cartItem?.find((item) => item.productOffering?.id === plan.planId);
    if (!cartItem) return;

    const hasAddOns = plan.addOns.length > 0;
    const action = await resolveEquipmentAction(plan.planId);
    const hasEquipment = action === 'show';

    if (!hasEquipment && !hasAddOns) return;

    editPlan.startEdit(cartItem);
  };

  const visibleSections = buildSectionConfigs({
    plansByCategory,
    filters,
    internetInCart,
    evOfferings,
    evInCart,
    regularPlansInCart,
  });

  if (isLoading || isCartLoading) {
    return <ProductOffersPageSkeleton />;
  }

  return (
    <Paper variant="pageFullBleed">
      <TopNavBar onShowCart={modals.handleShowCart} itemCount={cartSummary.itemCount} />
      <CheckAvailabilityDialog
        open={modals.showCheckAvailability}
        onClose={modals.handleCheckAvailabilityClose}
        onQualificationSuccess={modals.handleQualificationSuccess}
        onNavigateToLeadCapture={modals.handleNavigateToLeadCapture}
        initialView={modals.checkAvailabilityInitialView}
      />
      <LeadCaptureDialog
        open={modals.isLeadCaptureOpen}
        onClose={modals.handleLeadCaptureDismiss}
        onSuccess={modals.handleLeadCaptureSuccess}
      />
      {/* Hero Header Section */}
      <AddressHeader />
      {visibleSections.map((section, idx) => {
        const backgroundColor = idx % 2 === 0 ? theme.novus.bgCyanLight : theme.novus.bgCyanDark;
        const isFirst = idx === 0;
        const isLast = idx === visibleSections.length - 1;
        return (
          <PlansSection
            key={section.key}
            title={section.title}
            plans={section.plans}
            cart={cart}
            categoryType={section.key}
            categoryLabel={section.categoryLabel}
            backgroundColor={backgroundColor}
            isFirst={isFirst}
            isLast={isLast}
            hasInternet={section.hasInternet}
            isSelectDisabled={section.isSelectDisabled}
            isCartMutating={isCartMutating}
            pendingPlanId={cardPendingPlanId}
            onPlanSelected={handlePlanSelected}
            onEditPlan={handleEditPlan}
            onSwitchPlan={handleSwitchPlan}
            renderCardChildren={section.renderCardChildren}
          />
        );
      })}
      <EvBundleRelationshipDialog
        plan={bundleSelectionPlan}
        onConfirm={handleBundleRelationshipConfirmed}
        onDismiss={handleBundleRelationshipDismissed}
        isConfirming={isBundleConfirming}
        initialSelectedIds={bundleEditInitialIds}
      />
      <ConfigurePlanDialog
        editMode={editPlan.editConfig}
        hasUnsavedChanges={editPlan.hasUnsavedChanges}
        editLoading={editPlan.isSaving}
        onSaveComplete={editPlan.handleSaveStep}
        onCancelEdit={editPlan.handleCancelEdit}
      />
      <CartFooter
        onShowCart={modals.handleShowCart}
        onCheckout={() => handleCheckout('footer')}
        checkoutError={footerCheckoutError}
        isCheckoutPending={isPending}
        onOpenIntakeForm={modals.handleNavigateToLeadCapture}
        cartSummary={cartSummary}
      />
      <ShoppingCartDrawer
        open={modals.cartDrawerOpen}
        onClose={modals.handleCloseCart}
        isCheckoutPending={isPending}
        onCheckout={() => handleCheckout('drawer')}
        checkoutError={modals.drawerCheckoutError}
        onOpenIntakeForm={modals.handleNavigateToLeadCapture}
      />
    </Paper>
  );
};

export default ProductOffersPage;
